/**
 * Logic for calculating and displaying prices.
 */

import {
  Activity,
  CurrencyCode,
  Enrollment,
  EnrollmentInput,
  GiftEnrollment,
  Learner,
  PricingOffer,
  Section,
  SectionChecklist
} from "@outschool/gql-backend-generated";
import {
  BASE_LOCALE,
  CURRENCIES_INFO,
  SupportedLocale,
  USD_STATIC_EXCHANGE_RATE,
  convertFromUSDCents,
  convertMinorToMajorDenomination
} from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import _ from "lodash";

import * as ActivityShared from "./Activity";

export type Activity_PricingFragment = OngoingPricedActivity &
  Pick<
    Activity,
    "uid" | "size_max" | "size_min" | "weekly_meetings" | "isClub"
  >;

export type OngoingPricedActivity = Pick<
  Activity,
  "is_ongoing_weekly" | "price_cents" | "duration_weeks"
>;

type Enrollment_PricingFragment = Partial<
  Pick<Enrollment, "price_cents" | "priceCents">
> & {
  acceptGiftSlugId?: GiftEnrollment["acceptGiftSlugId"];
  isGift?: EnrollmentInput["isGift"];
};

type PricingBreakdownEnrollments = {
  enrollment?: Enrollment_PricingFragment;
  originalPrice: number;
  price: number;
  totalNumber?: number;
};

type PricingBreakdown = {
  acceptingGiftEnrollments?: Array<{
    learner: Learner;
  }>;
  enrollments?: Array<PricingBreakdownEnrollments>;
  giftEnrollments?: PricingBreakdownEnrollments;
  originalTotalPrice: number | null;
  totalPrice: number;
};

type PricingOffer_PricingFragment = Partial<
  Pick<PricingOffer, "uid" | "discount" | "remainingCapCents">
> & {
  price_cents?: Activity["price_cents"];
};

export type Section_PricingFragment = Pick<
  Section,
  "uid" | "price_cents" | "start_time"
> & {
  checklist?: Partial<SectionChecklist>;
};

// Increasing our fee from 17% to 30%
const SERVICE_FEE_INCREASE_DATE = "2018-02-01";
// Some sections are grandfathered but it has a max start time limit
const SERVICE_FEE_INCREASE_GRANDFATHERING_END_DATE = "2018-06-01";

const PERCENTAGE_FOR_TEACHER = 0.7;
const PERCENTAGE_FOR_TEACHER_BEFORE_FEB_2018 = 0.83;

export const REFERRAL_PROGRAM_START_DATE = "2018-07-16";
export const STORED_VALUE_REFERRAL_PROGRAM_START_DATE = "2021-01-06";
export const REFER_CREDIT_CENTS = 20 * 100;
export const REFER_TEACHER_CREDIT_CENTS = 200 * 100;
export const REFER_TEACHER_CREDIT_REQ_CENTS = 100 * 100;
export const MINIMUM_PRICE_CENTS = 100;
export const MAXIMUM_PRICE_CENTS = 300000; // $3k
export const MINIMUM_REFUND_CENTS = 10;

export function perStudentPriceInCents(
  activity: Pick<Activity, "price_cents">,
  section?: Section_PricingFragment | null
): number {
  if (section && section.price_cents !== null && section.price_cents >= 0) {
    return section.price_cents;
  }
  return activity?.price_cents ?? 0;
}

export function centsToNumericDollarString(priceInCents: number): string {
  if (!priceInCents) {
    return (0).toLocaleString();
  }

  return convertMinorToMajorDenomination({
    amount: priceInCents,
    currencyCode: CurrencyCode.Usd
  }).toLocaleString();
}

export function centsToDollars(priceInCents: number): number {
  if (!priceInCents) {
    return 0;
  }

  return priceInCents / 100.0;
}

/**
 * Wrapper around attachCurrencyDecoratorsToPrice that accepts a minor denomination
 * (e.g. cents, pence). Converts to the main denomination of the currency and returns
 * it as a formatted string. For USD and most other currencies, this will be dollars or the
 * dollar equivalent (2 decimals or "minor units"). For the Yen and the Won,
 * there are 0 decimals, so the minor unit is also the main denomination.
 *
 * @param {Object} Opts                                Options object to specify display options
 * @param {integer} Opts.amountInMinorCurrency         Price in minor currency
 * @param {CurrencyCode} Opts.currencyCode             Currency code (e.g. USD)
 * @param {boolean} Opts.displayNoCents                Do not display cents as part of total
 * @param {boolean} Opts.roundUp                       Round the price up to the nearest integer (100 for KRW/JPY)
 *
 * @return {string}                       Converted price from minor currency unit to
 *                                        major currency unit
 */

export function convertToCurrencyMainDenomination({
  amountInMinorCurrency,
  currencyCode,
  showCurrencyCode = true,
  displayNoCents = false,
  roundUp,
  locale = BASE_LOCALE
}: {
  amountInMinorCurrency: number;
  currencyCode: CurrencyCode;
  showCurrencyCode?: boolean;
  displayNoCents?: boolean;
  roundUp?: boolean;
  locale?: SupportedLocale;
}): string {
  const priceInMajorUnits = convertMinorToMajorDenomination({
    amount: amountInMinorCurrency,
    currencyCode
  });

  const priceHasNoCents = Math.floor(+priceInMajorUnits) === +priceInMajorUnits;
  const shouldDisplayNoCents = displayNoCents || priceHasNoCents;

  return attachCurrencyDecoratorsToPrice({
    price: priceInMajorUnits,
    showCurrencyCode,
    currencyCode,
    displayNoCents: shouldDisplayNoCents,
    roundUp,
    locale
  });
}

/**
 * Formats a given price with a currency symbol and a currency code (if
 * specified). For example: $100.30 USD or $100.30
 *
 * @param {Object} Opts                     Options object to specify display options
 * @param {integer} Opts.price              Price to display, in main denomination (e.g. dollars, pounds, won)
 * @param {CurrencyCode} Opts.currencyCode  Currency code (e.g. USD)
 * @param {boolean} Opts.showCurrencyCode   If true, prints the currency code after the price
 * @param {boolean} Opts.displayNoCents     If true, truncates any decimals from the price
 * @param {boolean} Opts.roundUp            If true, rounds the price up to the nearest integer (nearest 100 for KRW/JPY)
 *
 * @return {string}                       human-readable price string
 */

export function attachCurrencyDecoratorsToPrice({
  price,
  showCurrencyCode,
  currencyCode = CurrencyCode.Usd,
  displayNoCents,
  roundUp,
  locale = BASE_LOCALE
}: {
  price: number | string;
  showCurrencyCode: boolean;
  currencyCode?: CurrencyCode;
  displayNoCents?: boolean;
  roundUp?: boolean;
  locale?: SupportedLocale;
}): string {
  /* TODO update to use "roundingIncrement" from Intl.NumberFormat v3 once it's released
   * https://github.com/tc39/proposal-intl-numberformat-v3#new-roundingprecision-options-ecma-402-286
   */
  const { precision } = CURRENCIES_INFO[currencyCode];

  if (roundUp) {
    price = _.ceil(+price, precision - 2);
  }

  const fractionDigits = displayNoCents ? 0 : precision;

  const displayPrice = Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(+price);

  return `${displayPrice}${showCurrencyCode ? ` ${currencyCode}` : ""}`;
}

/**
 * Converts a price value (integer, in cents) to a human-readable string
 * like '$1' or '$12.99'.
 *
 * @param {Object} Opts                       Options object
 * @param {number} Opts.priceInCents          Integer value, denominated in cents
 * @param {boolean} Opts.useFree              Flag to specify "Free" instead of $0
 * @param {boolean} Opts.showCurrencyCode     Flag to specify if should price shows currency code
 * @param {boolean} Opts.displayNoCents       Do not display cents as part of total
 * @param {CurrencyCode} Opts.currencyCode    Currency code (e.g. USD)
 *
 * @return {string}                human-readable price string
 */

export function renderCurrencyString({
  priceInCents,
  useFree = false,
  showCurrencyCode = true,
  displayNoCents = false,
  currencyCode,
  roundUp,
  locale = BASE_LOCALE
}: {
  priceInCents: number;
  useFree?: boolean;
  showCurrencyCode?: boolean;
  displayNoCents?: boolean;
  currencyCode?: CurrencyCode;
  roundUp?: boolean;
  locale?: SupportedLocale;
}): string {
  // TODO: think about how we can default to USD, since the null value from
  // the database doesn't cause default parameters to be used.
  const currencyCodeWithFallback = currencyCode || CurrencyCode.Usd;

  if (priceInCents === 0) {
    if (useFree) {
      return "Free";
    }

    return attachCurrencyDecoratorsToPrice({
      price: 0,
      showCurrencyCode,
      currencyCode: currencyCodeWithFallback,
      displayNoCents: true,
      locale
    });
  } else {
    return convertToCurrencyMainDenomination({
      amountInMinorCurrency: priceInCents,
      showCurrencyCode,
      currencyCode: currencyCodeWithFallback,
      displayNoCents,
      roundUp,
      locale
    });
  }
}

/**
 * Wrapper around renderCurrencyString. Converts an amount in USD cents to a given
 * currency and then renders that currency as a formatted, human-readable string.
 *
 * Usage note: if this function does not receive both a currency code and
 * exchange rate, it always default to USD and not perform any currency
 * conversion.
 *
 * @param {Object} Opts                       Options object
 * @param {number} Opts.amountUSDCents        Integer value of USD cents
 * @param {number} Opts.exchangeRate          Exchange rate from USD to another currency
 * @param {CurrencyCode} Opts.currencyCode    Currency code (e.g. USD)
 *
 * @return {string}                human-readable price string
 */

export function renderCurrencyStringWithCurrencyConversion({
  amountUSDCents,
  exchangeRate,
  displayNoCents = false,
  roundUp = false,
  currencyCode,
  showCurrencyCode = true,
  useFree = false,
  showUSD = false
}: {
  amountUSDCents: number;
  exchangeRate?: number;
  displayNoCents?: boolean;
  roundUp?: boolean;
  currencyCode?: CurrencyCode;
  showCurrencyCode?: boolean;
  useFree?: boolean;
  showUSD?: boolean;
}): string {
  let priceToRender = amountUSDCents;

  if (showUSD) {
    exchangeRate = USD_STATIC_EXCHANGE_RATE;
    currencyCode = CurrencyCode.Usd;
  }

  if (exchangeRate && currencyCode) {
    priceToRender = convertFromUSDCents({
      amount: amountUSDCents,
      toExchangeRate: exchangeRate,
      toCurrencyCode: currencyCode
    });
  }

  return renderCurrencyString({
    priceInCents: priceToRender,
    showCurrencyCode,
    useFree,
    // Ensure that we converted currency before passing currencyCode to
    // renderCurrencyString. Otherwise, default to USD.
    displayNoCents,
    roundUp,
    currencyCode: exchangeRate && currencyCode ? currencyCode : CurrencyCode.Usd
  });
}

/**
 * Display the price in a human-readable form.
 * @param  {[type]} activity [description]
 * @param section
 * @param currencyCode
 * @param convert
 * @return {String}
 */
export function displayPerStudentPrice(
  activity: OngoingPricedActivity & Pick<Activity, "isClub">,
  section: Section_PricingFragment | null,
  currencyCode: CurrencyCode = CurrencyCode.Usd,
  convert: (price: number) => number = price => price,
  roundUp?: boolean,
  locale: SupportedLocale = BASE_LOCALE
): string {
  const price = convert(perStudentPriceInCents(activity, section));

  if (price) {
    const durationSuffix = activity.isClub
      ? " per month"
      : !!activity.is_ongoing_weekly
      ? " per week"
      : " total";
    return `${renderCurrencyString({
      priceInCents: price,
      showCurrencyCode: false,
      currencyCode,
      displayNoCents: roundUp,
      roundUp,
      locale
    })}${durationSuffix}`;
  } else {
    return renderCurrencyString({ priceInCents: price, useFree: true, locale });
  }
}

// How much the buyer pays for these enrollments.
// This is the total list price minus any pricing offers or vouchers,
// but refunds are not subtracted.
export function totalBuyerPriceCentsForEnrollments(
  enrollments: Array<Enrollment_PricingFragment>,
  pricingOffer?: PricingOffer_PricingFragment
): number {
  if (!enrollments) {
    return 0;
  }

  const sellerPrice = totalSellerPriceCentsForEnrollments(enrollments);
  if (!pricingOffer) {
    return sellerPrice;
  }
  let remainingCapCents = pricingOffer.remainingCapCents!;
  return enrollments.reduce(function (
    currentTotal: number,
    enrollment: Enrollment_PricingFragment
  ): number {
    const discountAmountCents = discountAmountForEnrollment(
      enrollment,
      pricingOffer,
      remainingCapCents
    );
    remainingCapCents -= discountAmountCents;
    return currentTotal + enrollment.price_cents! - discountAmountCents;
  },
  0);
}

export function buyerPriceCentsForEnrollments(
  enrollments: Array<Enrollment_PricingFragment>,
  pricingOffer?: PricingOffer_PricingFragment
): Array<number> {
  if (!enrollments) {
    return [];
  }
  if (!pricingOffer) {
    return enrollments.map(
      (enrollment: Enrollment_PricingFragment) => enrollment.price_cents!
    );
  }
  let remainingCapCents = pricingOffer.remainingCapCents!;
  return enrollments.map((enrollment: Enrollment_PricingFragment) => {
    const discountAmountCents = discountAmountForEnrollment(
      enrollment,
      pricingOffer,
      remainingCapCents
    );
    remainingCapCents -= discountAmountCents;
    return enrollment.price_cents! - discountAmountCents;
  });
}

function discountAmountForEnrollment(
  enrollment: Enrollment_PricingFragment,
  pricingOffer: PricingOffer_PricingFragment,
  remainingCapCents: number
): number {
  const enrollmentPrice = enrollment?.price_cents ?? 0;
  let discountAmountCents = 0;
  if (pricingOffer) {
    if (pricingOffer.discount) {
      discountAmountCents = Math.floor(pricingOffer.discount * enrollmentPrice);
    } else if (enrollmentPrice > 0) {
      discountAmountCents = enrollmentPrice;
    }
  }
  discountAmountCents = Math.min(remainingCapCents, discountAmountCents);
  return discountAmountCents;
}

export function pricingOfferAmountForEnrollments(
  enrollments: Array<Enrollment_PricingFragment>,
  pricingOffer: PricingOffer_PricingFragment
): number {
  return (
    totalSellerPriceCentsForEnrollments(enrollments) -
    totalBuyerPriceCentsForEnrollments(enrollments, pricingOffer)
  );
}

export function pricingBreakdownForBuyOutClass(
  enrollments: Array<Enrollment_PricingFragment>,
  activity: Activity_PricingFragment,
  buyOutPrice: number
): PricingBreakdown {
  const totalBuyOutPrice = (enrollments || []).length * buyOutPrice;
  const originalTotalPrice = (enrollments || []).length * activity.price_cents!;

  return {
    totalPrice: totalBuyOutPrice,
    originalTotalPrice,
    enrollments: (enrollments || []).map(
      (enrollment: Enrollment_PricingFragment) => ({
        enrollment,
        originalPrice: activity.price_cents!,
        price: buyOutPrice
      })
    ),
    // Gift enrollments for BuyOut classes not supported, yet.
    // However, this function and the below function pricingBreakdownForOrder
    // should have the same return signature.
    giftEnrollments: {
      totalNumber: 0,
      originalPrice: activity.price_cents!,
      price: buyOutPrice
    }
  };
}

export function pricingBreakdownForAcceptingGifts(
  learners: Array<Learner>
): PricingBreakdown {
  const [learner] = learners;
  return {
    totalPrice: 0,
    originalTotalPrice: 0,
    acceptingGiftEnrollments: [{ learner }]
  };
}

export function pricingBreakdownForOrder(
  enrollments: Array<Enrollment_PricingFragment>,
  pricingOffer: PricingOffer_PricingFragment
): PricingBreakdown {
  const originalTotalPrice = totalSellerPriceCentsForEnrollments(enrollments);
  const totalPrice = totalBuyerPriceCentsForEnrollments(
    enrollments,
    pricingOffer
  );
  const effectivePricingOffer =
    totalPrice !== originalTotalPrice ? pricingOffer : undefined;

  const [giftEnrollments, nonGiftEnrollments] = _.partition(
    enrollments,
    (e: Enrollment_PricingFragment) => e.isGift
  );

  const enrollmentPrices = buyerPriceCentsForEnrollments(
    nonGiftEnrollments,
    effectivePricingOffer
  );

  const totalGiftEnrollmentsPrice = _.sum(
    buyerPriceCentsForEnrollments(giftEnrollments, effectivePricingOffer)
  );

  return {
    totalPrice,
    originalTotalPrice,
    enrollments: nonGiftEnrollments.map(
      (enrollment: Enrollment_PricingFragment, idx: number) => ({
        enrollment,
        originalPrice: enrollment.price_cents ?? 0,
        price: enrollmentPrices[idx]
      })
    ),
    giftEnrollments: {
      totalNumber: giftEnrollments.length,
      originalPrice: _.sumBy(
        giftEnrollments,
        (ge: Enrollment_PricingFragment) => ge.price_cents ?? 0
      ),
      price: totalGiftEnrollmentsPrice
    }
  };
}

// How much the seller expects to receive for these enrollments.
// This is the total list price, not accounting for any Outschool pricing offers
// the buyer took advantage of (e.g. financial assistance offer)

export function totalSellerPriceCentsForEnrollments(
  enrollments: Array<Enrollment_PricingFragment>
): number {
  if (!enrollments) {
    return 0;
  }
  // TODO: Remove price_cents once all instances have been replaced.
  return enrollments.reduce(function (
    currentTotal: number,
    enrollment: Enrollment_PricingFragment
  ) {
    return (
      currentTotal + (enrollment.price_cents || enrollment.priceCents || 0)
    );
  },
  0);
}

export function standardPayoutCents(
  enrollmentVolumeCents: number,
  section: Section_PricingFragment
): number {
  const thresholdDate = dayjs.tz(
    SERVICE_FEE_INCREASE_DATE,
    Time.OUTSCHOOL_TIMEZONE
  );
  const sectionDate = dayjs(section.start_time);

  const grandfatheringEndDate = dayjs.tz(
    SERVICE_FEE_INCREASE_GRANDFATHERING_END_DATE,
    Time.OUTSCHOOL_TIMEZONE
  );

  const isGrandfathered =
    !!section.checklist &&
    !!section.checklist.grandfatheredAt2017Rate &&
    sectionDate.isBefore(grandfatheringEndDate);

  let payoutInCents;
  if (!isGrandfathered && sectionDate.isAfter(thresholdDate)) {
    payoutInCents = 1.0 * enrollmentVolumeCents * PERCENTAGE_FOR_TEACHER;
  } else {
    payoutInCents =
      1.0 * enrollmentVolumeCents * PERCENTAGE_FOR_TEACHER_BEFORE_FEB_2018;
  }
  // We cannot pay a fraction of a cent
  return Math.round(payoutInCents);
}

export function parseDollarStringToCents(dollarString: string): number {
  let parsedFloat = parseFloat(dollarString);
  if (isNaN(parsedFloat)) {
    return parsedFloat;
  }
  return Math.round(parsedFloat * 100);
}

export function estimatedWeeklyEarningsString(
  activity: OngoingPricedActivity &
    Pick<Activity, "size_min" | "size_max" | "hasTeacherSchedule">
): string {
  const cents = centsPerLearnerPerWeek(activity);
  const lowerCents = activity.size_min! * cents * PERCENTAGE_FOR_TEACHER;
  const upperCents = activity.size_max! * cents * PERCENTAGE_FOR_TEACHER;

  if (activity?.hasTeacherSchedule) {
    const priceCents = activity.price_cents! * PERCENTAGE_FOR_TEACHER;
    return `$${Math.round(centsToDollars(priceCents))} USD per session`;
  }

  return `$${Math.round(centsToDollars(lowerCents))} - ${Math.round(
    centsToDollars(upperCents)
  )} USD per week`;
}

export function centsPerLearnerPerWeek(
  activity: OngoingPricedActivity
): number {
  // This will only be called on published activities, which will have all these fields
  return !!activity.is_ongoing_weekly
    ? activity.price_cents!
    : activity.price_cents! / activity.duration_weeks!;
}

export function centsPerLearnerPerMeeting(
  activity: OngoingPricedActivity &
    Pick<Activity, "isClub" | "weekly_meetings" | "hasTeacherSchedule">
): number {
  // This will only be called on published activities, which will have all these fields
  if (activity.isClub) {
    // There are no "meetings" for clubs/interest groups.
    return activity.price_cents!;
  } else if (activity?.hasTeacherSchedule) {
    return activity.price_cents!;
  } else if (!!activity.is_ongoing_weekly) {
    return activity.price_cents! / activity.weekly_meetings!;
  } else if (ActivityShared.isFlexSchedule(activity)) {
    return activity.price_cents! / activity.duration_weeks!;
  } else {
    return (
      activity.price_cents! /
      (activity.duration_weeks! * activity.weekly_meetings!)
    );
  }
}

export function makePerMeetingPriceText(
  pricePerMeetingMinor: number,
  currencyCode: CurrencyCode,
  roundUp,
  locale: SupportedLocale = BASE_LOCALE
): string {
  const pricePerMeeting = parseFloat(
    convertMinorToMajorDenomination({
      amount: pricePerMeetingMinor,
      currencyCode
    })
  );
  if (pricePerMeeting > 0 && pricePerMeeting < 1) {
    return `<${attachCurrencyDecoratorsToPrice({
      price: 1,
      showCurrencyCode: false,
      currencyCode,
      displayNoCents: true,
      locale
    })}`;
  }

  return `${attachCurrencyDecoratorsToPrice({
    price: pricePerMeeting,
    showCurrencyCode: false,
    currencyCode,
    displayNoCents: true,
    roundUp,
    locale
  })}`;
}
